import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/common/form-field";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import type { Faq, FaqInput } from "@/features/knowledge/types";

const schema = Yup.object({
  question: Yup.string().trim().required("A question is required.").max(200),
  answer: Yup.string()
    .transform((v) => v?.replace(/<[^>]*>/g, "").trim())
    .required("An answer is required."),
  displayOrder: Yup.number().min(0).required(),
});

/** Formik-bound rich-text field for the FAQ answer. */
function AnswerField() {
  const [field, meta, helpers] = useField<string>("answer");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label>Answer</Label>
      <RichTextEditor value={field.value} onChange={(html) => helpers.setValue(html)} />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface FaqFormDialogProps {
  faq: Faq | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: FaqInput) => Promise<void>;
}

export function FaqFormDialog({ faq, open, onOpenChange, onSubmit }: FaqFormDialogProps) {
  const initial: FaqInput = {
    question: faq?.question ?? "",
    answer: faq?.answer ?? "",
    displayOrder: faq?.displayOrder ?? 0,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{faq ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={initial}
          validationSchema={schema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSubmit(values);
              onOpenChange(false);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <FormField name="question" label="Question" placeholder="e.g. What is an OIC?" />
              <AnswerField />
              <FormField name="displayOrder" label="Display order" type="number" min={0} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
